export type TableColumnAlign = 'left' | 'center' | 'right';

export type TableColumnDef = {
  align?: TableColumnAlign;
  width?: string;
};

export type ColgroupColumnStyle = {
  align?: TableColumnAlign;
  width?: string;
};

export type MobileColumnLayout =
  | { kind: 'fixed'; width: number }
  | { kind: 'percent'; percent: number }
  | { kind: 'flex'; flex: number; minWidth: number };

const DefaultMobileMinCellWidth = 120;

/** Stored `Npx` values map 1:1 to React Native layout points on mobile. */
function percentToPoints(
  percent: number,
  percentReferenceWidth: number,
): number {
  return Math.round((percentReferenceWidth * percent) / 100);
}

/** Treat empty, auto, and whitespace-only values as no explicit width. */
export function normalizeColumnWidth(width?: string): string | undefined {
  const trimmed = width?.trim();
  if (!trimmed || trimmed === 'auto') {
    return undefined;
  }
  return trimmed;
}

/** Normalize user-entered width strings before persisting (e.g. `120` → `120px`). */
export function normalizeColumnWidthInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'auto') {
    return '';
  }
  const spacedUnit = /^(\d+(?:\.\d+)?)\s*(px|%)$/i.exec(trimmed);
  if (spacedUnit) {
    return `${spacedUnit[1]}${spacedUnit[2].toLowerCase()}`;
  }
  const collapsed = trimmed.replace(/\s+/g, '');
  if (/%$|px$/i.test(collapsed)) {
    return collapsed;
  }
  if (/^\d+(\.\d+)?$/.test(collapsed)) {
    return `${collapsed}px`;
  }
  return collapsed;
}

export function getColumnAlign(
  columns: TableColumnDef[] | undefined,
  colIndex: number,
): TableColumnAlign | undefined {
  return columns?.[colIndex]?.align;
}

export function getColumnWidthStyle(
  columns: TableColumnDef[] | undefined,
  colIndex: number,
): string | undefined {
  return normalizeColumnWidth(columns?.[colIndex]?.width);
}

export function buildColgroupStyles(
  columns: TableColumnDef[] | undefined,
  colCount: number,
): ColgroupColumnStyle[] {
  return Array.from({ length: colCount }, (_, colIndex) => {
    const align = getColumnAlign(columns, colIndex);
    const width = getColumnWidthStyle(columns, colIndex);
    return {
      ...(align !== undefined ? { align } : {}),
      ...(width !== undefined ? { width } : {}),
    };
  });
}

export function tableHasExplicitColumnWidths(
  columns: TableColumnDef[] | undefined,
  colCount: number,
): boolean {
  return buildColgroupStyles(columns, colCount).some(
    col => col.width !== undefined,
  );
}

export function tableHasPercentColumnWidths(
  columns: TableColumnDef[] | undefined,
  colCount: number,
): boolean {
  return buildColgroupStyles(columns, colCount).some(col =>
    col.width?.endsWith('%'),
  );
}

export function computeMobilePercentReferenceWidth(
  screenWidth: number,
  screenHeight: number,
): number {
  return Math.max(screenWidth, screenHeight);
}

function parsePxWidth(rawWidth: string): number | undefined {
  if (!rawWidth.endsWith('px')) {
    return undefined;
  }
  const parsed = parseFloat(rawWidth);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parsePercentWidth(rawWidth: string): number | undefined {
  if (!rawWidth.endsWith('%')) {
    return undefined;
  }
  const parsed = parseFloat(rawWidth);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function allColumnsFixedPx(
  columns: TableColumnDef[] | undefined,
  colCount: number,
): boolean {
  if (colCount <= 0) {
    return false;
  }
  for (let colIndex = 0; colIndex < colCount; colIndex += 1) {
    const rawWidth = normalizeColumnWidth(columns?.[colIndex]?.width);
    if (!rawWidth || parsePxWidth(rawWidth) === undefined) {
      return false;
    }
  }
  return true;
}

export function resolveMobileTableWidth(
  columns: TableColumnDef[] | undefined,
  colCount: number,
  options: {
    percentReferenceWidth: number;
  },
): {
  tableWidth?: number;
  percentReferenceWidth?: number;
} {
  const { percentReferenceWidth } = options;

  if (tableHasPercentColumnWidths(columns, colCount)) {
    return { tableWidth: percentReferenceWidth, percentReferenceWidth };
  }

  if (allColumnsFixedPx(columns, colCount)) {
    let sum = 0;
    for (let colIndex = 0; colIndex < colCount; colIndex += 1) {
      const rawWidth = normalizeColumnWidth(columns?.[colIndex]?.width);
      const pxWidth = rawWidth ? parsePxWidth(rawWidth) : undefined;
      if (pxWidth !== undefined) {
        sum += Math.max(pxWidth, 0);
      }
    }
    return { tableWidth: sum };
  }

  return { tableWidth: undefined };
}

export function resolveMobileColumnLayout(
  columns: TableColumnDef[] | undefined,
  colIndex: number,
  options: {
    minWidth?: number;
  } = {},
): MobileColumnLayout {
  const minWidth = options.minWidth ?? DefaultMobileMinCellWidth;
  const rawWidth = normalizeColumnWidth(columns?.[colIndex]?.width);

  if (!rawWidth) {
    return { kind: 'flex', flex: 1, minWidth };
  }

  const pxWidth = parsePxWidth(rawWidth);
  if (pxWidth !== undefined) {
    return {
      kind: 'fixed',
      width: Math.max(pxWidth, 0),
    };
  }

  const percent = parsePercentWidth(rawWidth);
  if (percent !== undefined) {
    return {
      kind: 'percent',
      percent,
    };
  }

  return { kind: 'flex', flex: 1, minWidth };
}

const AutoColumnMinWidth = DefaultMobileMinCellWidth;
const AutoColumnMaxWidth = 320;
/** Rough average glyph advance at body font size — good enough for layout, not typography. */
const AutoColumnCharWidthPx = 8;
/** Matches the cell shell's horizontal padding (`px-6` = 24px each side). */
const AutoColumnPaddingPx = 48;

/**
 * Estimate a fixed pixel width for an auto-sized column from its widest
 * cell's character count. React Native has no `<colgroup>` — every row is an
 * independent flex container — so an "auto" column resolves to `flex: 1` and
 * ends up a different width in every row (content-driven). Giving every
 * such column the SAME fixed width (derived once from content across the
 * whole table) is what makes cells line up down the column.
 */
export function estimateAutoColumnWidth(
  maxChars: number,
  options: { minWidth?: number; maxWidth?: number } = {},
): number {
  const minWidth = options.minWidth ?? AutoColumnMinWidth;
  const maxWidth = options.maxWidth ?? AutoColumnMaxWidth;
  const estimated = maxChars * AutoColumnCharWidthPx + AutoColumnPaddingPx;
  return Math.min(maxWidth, Math.max(minWidth, Math.round(estimated)));
}

/** Per-column counterpart of {@link estimateAutoColumnWidth}. */
export function computeAutoColumnWidths(
  maxCharsByColumn: number[],
  options: { minWidth?: number; maxWidth?: number } = {},
): number[] {
  return maxCharsByColumn.map(chars => estimateAutoColumnWidth(chars, options));
}

export function resolveMobileCellWidth(
  columns: TableColumnDef[] | undefined,
  colIndex: number,
  colSpan: number,
  options: {
    minWidth?: number;
    percentReferenceWidth?: number;
  } = {},
): {
  width?: number;
  flex?: number;
  minWidth?: number;
} {
  const { percentReferenceWidth } = options;

  if (colSpan <= 1) {
    const layout = resolveMobileColumnLayout(columns, colIndex, options);
    if (layout.kind === 'fixed') {
      return { width: layout.width };
    }
    if (layout.kind === 'percent') {
      if (percentReferenceWidth === undefined) {
        return {
          flex: 1,
          minWidth: options.minWidth ?? DefaultMobileMinCellWidth,
        };
      }
      return {
        width: percentToPoints(layout.percent, percentReferenceWidth),
      };
    }
    return { flex: layout.flex, minWidth: layout.minWidth };
  }

  let totalFixed = 0;
  let totalPercent = 0;
  let totalFlex = 0;
  let minWidth = options.minWidth ?? DefaultMobileMinCellWidth;

  for (let offset = 0; offset < colSpan; offset += 1) {
    const layout = resolveMobileColumnLayout(
      columns,
      colIndex + offset,
      options,
    );
    if (layout.kind === 'fixed') {
      totalFixed += layout.width;
    } else if (layout.kind === 'percent') {
      totalPercent += layout.percent;
    } else {
      totalFlex += layout.flex;
      minWidth = Math.max(minWidth, layout.minWidth);
    }
  }

  if (totalFixed > 0 && totalPercent === 0 && totalFlex === 0) {
    return { width: totalFixed };
  }
  if (totalPercent > 0 && totalFixed === 0 && totalFlex === 0) {
    if (percentReferenceWidth === undefined) {
      return { flex: 1, minWidth };
    }
    return {
      width: percentToPoints(
        Math.min(totalPercent, 100),
        percentReferenceWidth,
      ),
    };
  }
  if (totalFlex > 0 && totalFixed === 0 && totalPercent === 0) {
    return { flex: totalFlex, minWidth };
  }
  return { width: Math.max(totalFixed, minWidth * colSpan) };
}
